import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { ImagePreview } from '@/components';
import { PackageMedia, PackageRow } from '@/services/api/packages';
import { ConditionBadge, PackageStatusBadge } from './PackageStatusBadge';
import PackageTimeline from './PackageTimeline';

const fmt = (v?: string | null) =>
  v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—';

// POD photos arrive grouped by `mediaType` (Dynamic Form per klien). Map the
// known kinds to human labels; unknown types fall back to a humanized form so
// new kinds render without a code change. Order controls display sequence.
const MEDIA_TYPE_LABELS: Record<string, string> = {
  DELIVERY_PROOF: 'Foto Barang',
  DELIVERY_NOTE: 'Surat jalan',
};
const MEDIA_TYPE_ORDER = Object.keys(MEDIA_TYPE_LABELS);

const humanize = (type: string) =>
  MEDIA_TYPE_LABELS[type] ??
  type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const groupMedia = (media: PackageMedia[]) => {
  const groups = new Map<string, PackageMedia[]>();
  for (const m of media) {
    if (!m?.url) continue;
    const bucket = groups.get(m.mediaType);
    if (bucket) bucket.push(m);
    else groups.set(m.mediaType, [m]);
  }
  return Array.from(groups.entries()).sort((a, b) => {
    const ia = MEDIA_TYPE_ORDER.indexOf(a[0]);
    const ib = MEDIA_TYPE_ORDER.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
};

const Section: React.FC<{
  title: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="border-b border-stroke-soft-200 px-6 py-5 last:border-b-0">
    <h3 className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
      {title}
    </h3>
    {children}
  </div>
);

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({
  k,
  children,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
    <span className="text-text-sub-600">{k}</span>
    <span className="text-right font-semibold text-text-strong-950">
      {children}
    </span>
  </div>
);

interface PackageDetailDrawerProps {
  pkg: PackageRow | null;
  onClose: () => void;
}

const PackageDetailDrawer: React.FC<PackageDetailDrawerProps> = ({
  pkg,
  onClose,
}) => {
  useEffect(() => {
    if (!pkg) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pkg, onClose]);

  if (!pkg) return null;

  const media = pkg.media ?? [];
  const groups = groupMedia(media);
  const podTotal = groups.reduce((n, [, items]) => n + items.length, 0);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 z-50 flex h-full w-[560px] max-w-[95vw] flex-col bg-bg-white-0 shadow-card-lg">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-stroke-soft-200 px-6 py-5">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-1.5">
              <PackageStatusBadge status={pkg.status} />
              {/* Damage rides alongside the status (Status Model v2 §4). */}
              <ConditionBadge condition={pkg.condition} />
            </div>
            <h2
              className="font-mono text-xl font-semibold tracking-tight text-text-strong-950"
              style={{ wordBreak: 'break-all' }}
            >
              {pkg.po}
            </h2>
            <p className="mt-0.5 font-mono text-xs text-text-soft-400">
              {pkg.bookingId ? (
                <>
                  {'Booking ID · '}
                  <span className="font-semibold text-text-sub-600">
                    {pkg.bookingId}
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Package */}
          <Section title="Package">
            <KV k="PO / Item">
              <span className="font-mono">{pkg.po}</span>
            </KV>
            {pkg.itemName && pkg.itemName !== pkg.po && (
              <KV k="Item name">{pkg.itemName}</KV>
            )}
            <KV k="Waybill">
              <span className="font-mono">{pkg.deliveryId || '—'}</span>
            </KV>
            <KV k="Booking ID">
              <span className="font-mono">{pkg.bookingId || '—'}</span>
            </KV>
            <KV k="Recipient (outlet)">{pkg.outlet || '—'}</KV>
            <KV k="Sender">{pkg.sender || '—'}</KV>
            <KV k="Qty · berat">
              {pkg.qty} pcs · {pkg.weight} kg
            </KV>
            <KV k="Koli">{pkg.koli ?? '—'}</KV>
            {pkg.recipientAddress && (
              <KV k="Alamat penerima">
                <span className="font-normal text-text-sub-600">
                  {pkg.recipientAddress}
                </span>
              </KV>
            )}
            <KV k="Created">{fmt(pkg.createdAt)}</KV>
          </Section>

          {/* Proof of Delivery */}
          <Section
            title={
              <span>
                Proof of Delivery{' '}
                <span className="font-medium normal-case text-text-soft-400">
                  {podTotal ? ` · ${podTotal} foto` : ''}
                </span>
              </span>
            }
          >
            {groups.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-stroke-soft-200 py-8 text-text-soft-400">
                <i className="ri-image-line text-2xl" />
                <span className="text-sm">Belum ada Proof Of Delivery</span>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {groups.map(([type, items]) => (
                    <div key={type}>
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-text-strong-950">
                          {humanize(type)}
                        </span>
                        {items.length > 1 && (
                          <span className="inline-flex items-center rounded-full bg-feature-lighter px-2 py-0.5 text-[10px] font-semibold text-feature-dark">
                            {items.length} foto
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {items.map((m, i) => (
                          <div key={m.id} className="w-[76px]">
                            <ImagePreview
                              src={m.url}
                              alt={`${humanize(type)} ${i + 1}`}
                              className="h-[76px] w-[76px] rounded-lg border border-stroke-soft-200 object-cover"
                            />
                            <div className="mt-1 truncate text-center text-[10px] text-text-soft-400">
                              {humanize(type)}
                              {items.length > 1 ? ` · ${i + 1}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-bg-weak-50 px-3 py-2.5 text-[11.5px] leading-snug text-text-sub-600">
                  <i className="ri-information-line mt-0.5 shrink-0 text-sm text-text-soft-400" />
                  <span>
                    Jumlah &amp; jenis foto mengikuti{' '}
                    <b>Dynamic Form per klien</b>. <b>Foto barang</b> bisa lebih
                    dari satu; <b>Surat jalan/terima</b> hanya tampil bila
                    diunggah. Klik untuk perbesar.
                  </span>
                </div>
              </>
            )}
          </Section>

          {/* Timeline — item status history (intake → dispatch → delivery,
              branching to the retur legs IN_RETURN → RETURNED_TO_HUB). */}
          <Section title="Timeline">
            <PackageTimeline events={pkg.timeline ?? []} />
          </Section>
        </div>
      </div>
    </>,
    document.body
  );
};

export default PackageDetailDrawer;
